import React from "react";
import { Plus, MessageSquarePlus, Eye } from "lucide-react";
import { Input, Textarea, Select, RepeatableGroup } from "./FormComponents";

const CATEGORY_OPTIONS = [
  "Arrival & Access",
  "Check-in",
  "Check-out",
  "Guest Services",
  "House Rules",
  "WiFi & Tech",
  "Local Guide",
  "Safety",
  "Policies",
  "Other",
];

const createItem = () => ({
  id: Date.now().toString(),
  title: "",
  category: "",
  information: "",
});

export const Step17CustomKnowledge = ({ data, updateData, isLight }) => {
  const items = data?.items || [];

  const updateItems = (nextItems) => {
    updateData("customKnowledge", { ...data, items: nextItems });
  };

  const addItem = () => {
    updateItems([...items, createItem()]);
  };

  const updateItem = (id, field, value) => {
    updateItems(items.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  };

  const removeItem = (index) => {
    updateItems(items.filter((_, i) => i !== index));
  };

  const renderItem = (itemData, index) => {
    const item = items[index];

    return (
      <div className="flex flex-col gap-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Title"
            value={item.title}
            onChange={(e) => updateItem(item.id, "title", e.target.value)}
            placeholder="Package deliveries"
            isLight={isLight}
          />
          <Select
            label="Category"
            value={item.category}
            onChange={(e) => updateItem(item.id, "category", e.target.value)}
            options={CATEGORY_OPTIONS}
            isLight={isLight}
          />
        </div>

        <Textarea
          label="Information (Factual Statement)"
          value={item.information}
          onChange={(e) => updateItem(item.id, "information", e.target.value)}
          placeholder="The outdoor pool is open from 8:00 AM to 10:00 PM daily. The pool is heated and available year-round. Towels are provided at the pool deck."
          isLight={isLight}
        />
      </div>
    );
  };

  // Map items to include 'name' for RepeatableGroup's header display
  const repeatableItems = items.map(item => ({
    ...item,
    name: item.title || "Untitled knowledge item"
  }));

  return (
    <div className="flex flex-col gap-6">
      <div className={`rounded-3xl backdrop-blur-2xl border p-5 sm:p-6 flex flex-col gap-4 ${
        isLight
          ? "bg-white border-neutral-200 shadow-[0_18px_40px_rgba(15,23,42,0.12)]"
          : "bg-white/5 border-white/5 shadow-[0_24px_70px_rgba(0,0,0,0.75)]"
      }`}>
        <div className="flex items-start gap-4">
          <div className={`h-10 w-10 rounded-2xl flex items-center justify-center border ${
            isLight ? "bg-neutral-50 border-neutral-200 text-neutral-600" : "bg-white/5 border-white/10 text-neutral-300"
          }`}>
            <MessageSquarePlus size={18} />
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-[0.18em] mb-0.5 ${
              isLight ? "text-neutral-500" : "text-neutral-400"
            }`}>
              Flexible Knowledge
            </div>
            <h3 className="text-base font-semibold tracking-tight">Custom Knowledge</h3>
            <p className={`mt-1 text-sm leading-relaxed max-w-2xl ${
              isLight ? "text-neutral-600" : "text-neutral-400"
            }`}>
              Add property-specific information that does not fit the standard form. Each item becomes a focused answer the assistant can use later.
            </p>
          </div>
        </div>
      </div>

      <div className={`rounded-3xl backdrop-blur-2xl border p-5 sm:p-6 flex flex-col gap-6 ${
        isLight
          ? "bg-white border-neutral-200 shadow-[0_18px_40px_rgba(15,23,42,0.12)]"
          : "bg-white/5 border-white/5 shadow-[0_24px_70px_rgba(0,0,0,0.75)]"
      }`}>
        <RepeatableGroup
          label="Custom Knowledge Items"
          items={repeatableItems}
          onAdd={addItem}
          onRemove={removeItem}
          renderItem={renderItem}
          isLight={isLight}
        />
      </div>
    </div>
  );
};
